import { colors } from '@kmns-internal/furikake/tokens'
import { Info, OpenInNew } from '@mui/icons-material'
import { Button, Stack, Typography } from '@mui/material'
import { FeatureNameEnum } from '@ulysses-inc/harami_api_client'
import { useFeature } from 'src/features/featureFlags/useFeatureFlags'
import { useBannerHeight } from 'src/hooks/useBannerHeight'
import { useElementHeight } from 'src/hooks/useElementHeight'

/** 記録アプリ (Web) への切り替えの案内記事 */
const GUIDE_URL =
  'https://docs.channel.io/kaminashireport/ja/articles/%E8%A8%98%E9%8C%B2%E3%82%A2%E3%83%97%E3%83%AAWeb%E5%88%87%E6%9B%BF%E3%81%AE%E3%81%94%E6%A1%88%E5%86%85-0d1de90c'

/**
 * 記録アプリ (iOS/iPadOS) の提供終了を予告するバナー。表示する企業は Feature Flag で指定する。
 * 移行を確実に促すため、閉じるボタンは置かない。
 *
 * @feature-flag:iosAppShutdownBanner
 * 削除時: このディレクトリを削除し、GlobalNavigation.tsx の <IosAppShutdownBanner /> を消す。
 */
export const IosAppShutdownBanner = () => {
  const { canUse } = useFeature(FeatureNameEnum.IOS_APP_SHUTDOWN_BANNER)
  const isVisible = canUse === 'yes'

  // 画面幅によって文言が折り返し、高さが変わるため、実際の高さを各画面に渡す
  const { ref, height } = useElementHeight()
  useBannerHeight(height)

  if (!isVisible) return null

  return (
    <Stack
      ref={ref}
      component="section"
      aria-label="記録アプリ (iOS/iPadOS) の提供終了のお知らせ"
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      sx={{
        px: 3,
        py: 1.5,
        columnGap: 3,
        rowGap: 1,
        bgcolor: colors.red[1],
      }}
    >
      {/* 文言の幅が 360px を切るときはボタンを下の行に回し、文言に横幅を譲る */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flex: '1 1 360px', minWidth: 0, columnGap: 2 }}
      >
        <Info sx={{ color: colors.error.main }} />

        {/* 日本語が文字の途中で折り返さないよう、<wbr /> の位置でだけ折り返す */}
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="baseline"
          sx={{
            minWidth: 0,
            columnGap: 2,
            wordBreak: 'keep-all',
            overflowWrap: 'anywhere',
          }}
        >
          <Typography
            variant="subtitle1"
            color={colors.error.main}
            sx={{ lineHeight: 1.5 }}
          >
            まもなく
            <wbr />
            記録アプリ&nbsp;(iOS/iPadOS)&nbsp;が
            <wbr />
            ご利用
            <wbr />
            できなくなります
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            11/30&nbsp;(月)&nbsp;までに
            <wbr />
            記録アプリ&nbsp;(Web)&nbsp;に
            <wbr />
            切替してください
          </Typography>
        </Stack>
      </Stack>

      <Button
        variant="outlined"
        color="secondary"
        component="a"
        href={GUIDE_URL}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNew />}
        sx={{ flexShrink: 0, ml: 'auto' }}
      >
        詳細はこちら
      </Button>
    </Stack>
  )
}
